import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, ChevronUp, Columns3, Filter } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type DragEvent as ReactDragEvent,
    type ReactNode,
    type Ref,
} from 'react';
import {
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type ColumnSizingState,
    type OnChangeFn,
    type SortingState,
    type VisibilityState,
} from '@tanstack/react-table';
import Pagination from './Pagination';
import Checkbox from './Checkbox';
import Button from './Button';
import EmptyState from '@/components/EmptyState';
import { useFillToBottom } from './use-fill-to-bottom';
import ColumnChooserPopover, { type ColumnChooserItem } from './ColumnChooserPopover';
import ColumnFilterPopover from './ColumnFilterPopover';
import DataTableFilterBar from './DataTableFilterBar';
import { clockNow } from '@/lib/clock';
import { useSignedInUserId } from '@/contexts/SessionContext';
import { buildTableView, type TableView, type TableViewValue } from './data-table-export';
import {
    applyColumnFilters,
    describeFilter,
    distinctValues,
    resolveFilterSpec,
    serialiseModel,
    type ColumnFilter,
    type ColumnFilterModel,
    type FilterScalar,
    type FilterType,
    type ResolvedFilterSpec,
} from './data-table-filter';
import {
    EMPTY_COLUMN_PREFS,
    columnPrefsStorageKey,
    defaultColumnsKey,
    isDefaultColumns,
    isEmptyColumnPrefs,
    moveColumn,
    moveColumnBy,
    parseColumnPrefs,
    setColumnWidth,
    storedColumnWidth,
    serialiseColumnPrefs,
    showAllColumns,
    orderedColumnIds,
    toggleColumn,
    visibleColumnIds,
    type ChooserColumn,
    type ColumnPrefs,
} from './data-table-columns';

export type {
    ColumnFilter,
    ColumnFilterModel,
    FilterScalar,
    FilterType,
} from './data-table-filter';

/** A single-column sort: the `DataTableColumn.id` plus a direction. */
export interface DataTableSort {
    id: string;
    desc: boolean;
}

export interface DataTableColumn<T> {
    /** Stable column id (also the sort key). */
    id: string;
    header: ReactNode;
    cell: (row: T) => ReactNode;
    align?: 'left' | 'right' | 'center';
    /** Numeric column: font-mono tabular-nums + right-align by default. */
    numeric?: boolean;
    sortable?: boolean;
    /** Sort key extractor; required for the column to sort meaningfully. */
    sortValue?: (row: T) => string | number | null;
    /** The widest the column starts. The content can be narrower. */
    width?: string;
    /** The width of the column while the table has no rows to measure. When
     *  rows exist, the column starts at the width of its content, so a guess
     *  here never adds empty space to a full table. */
    minWidth?: string;
    /** false = no filter funnel on this column. The default is a funnel when the
     *  column has a `filterValue` or a `sortValue`. */
    filterable?: boolean;
    /** The filter type. The default is inferred from the data. */
    filterType?: FilterType;
    /** The value that the filter reads. The default is `sortValue`. Give it when
     *  `sortValue` is not the real value, for example a day count or a rank. */
    filterValue?: (row: T) => FilterScalar;
    /** The text of a value in the value list and in the filter chip. The default
     *  is the formatted value. */
    filterLabel?: (row: T) => string;
    /** A closed set of values. This makes the column an enum filter. */
    enumOptions?: readonly { value: string; label: string }[];
    /** false = the column chooser cannot hide this column. Give it to the
     *  column that names the row. The default is true. */
    hideable?: boolean;
    /** true = the column is in the column chooser but does not show until the
     *  reader adds it. Use it for a field that is useful but not needed on
     *  every visit. */
    defaultHidden?: boolean;
    /** false = the reader cannot move this column, and it keeps its default
     *  place. A column with no header text (a row action, a status dot) never
     *  moves. The default is true. */
    reorderable?: boolean;
    /** true = the column takes the width that the table has spare, when the
     *  table is narrower than its frame. When no column sets it, the first
     *  left-aligned column with a header grows, so that a figure stays near its
     *  header (SEP-295). false = the column never takes that default. A width
     *  that the reader sets by a resize still wins. */
    grow?: boolean;
    /** The value of the cell in an export of the view (SEP-298). Give a number
     *  for a figure, so that a spreadsheet keeps it as a number. The default is
     *  the label of an enum value, then `filterLabel`, then `filterValue`, then
     *  `sortValue`, then the cell when it is plain text or a number. Give it
     *  when `sortValue` is a rank and not the value that the cell shows. */
    exportValue?: (row: T) => TableViewValue;
}

/** The handle that `viewRef` gives: the view of the table, for an export. */
export interface DataTableViewHandle {
    /** The visible columns in the order of the reader, and every row that
     *  passes the filters, in the current sort. It ignores the page. */
    getView: () => TableView;
}

/**
 * Row selection. The table renders a leading checkbox column and owns the
 * event plumbing; the caller owns the selected set.
 *
 * The plumbing is the reason this lives in the kit rather than in a caller's
 * column list. `Checkbox` already stops a CLICK from reaching the row, but a
 * clickable row also carries `tabIndex` and a Space/Enter `onKeyDown`, so a
 * keyboard user who tabs to a checkbox and presses Space would toggle the box
 * AND navigate away. The cell below stops both events. A caller-built column
 * cannot state that reason next to the code that needs it.
 */
export interface DataTableSelection<T> {
    selectedKeys: ReadonlySet<string>;
    onToggle: (key: string, row: T) => void;
    /** Select every selectable row in view, or clear them when all are already in
     *  the set. `visibleKeys` holds the selectable keys of the FILTERED rows, so
     *  a select-all under a column filter never takes a row the reader cannot
     *  see. A selected row that a filter hides stays selected. */
    onToggleAll: (visibleKeys: readonly string[]) => void;
    /** Rows a caller may select. A row that fails it renders an empty cell and
     *  never counts towards the header checkbox. Defaults to every row. */
    isSelectable?: (row: T) => boolean;
    /** Accessible label for one row's checkbox. */
    rowLabel?: (row: T) => string;
    /** Accessible label for the header checkbox. */
    allLabel?: string;
}

export interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    data: T[];
    rowKey: (row: T) => string;
    onRowClick?: (row: T) => void;
    /** Extra classes applied per data row (e.g. a status accent band). */
    rowClassName?: (row: T) => string | undefined;
    selectedKey?: string;
    /** Uncontrolled: the sort the table opens with, owned thereafter by the
     * table itself. Ignored when `sort` is supplied. */
    initialSort?: DataTableSort;
    /** Controlled sort. Supplying this prop (including as `null`, meaning
     * "unsorted") hands ownership of the sort to the caller: the table renders
     * exactly this and never mutates it, so pair it with `onSortChange`. Use it
     * to survive a remount — see `useTableViewState`. Omit both props for the
     * default self-owned behaviour seeded from `initialSort`. */
    sort?: DataTableSort | null;
    /** Fires on every header sort toggle, in both controlled and uncontrolled
     * mode. `null` = the user cycled the sort off. */
    onSortChange?: (sort: DataTableSort | null) => void;
    stickyHeader?: boolean;
    /** Expanded content (chevron + row spanning all columns), animated. */
    expandable?: (row: T) => ReactNode;
    /** Leading checkbox column. The header checkbox counts every SELECTABLE row
     *  that passes the column filters, and not only the rows on the current
     *  page, so a select-all takes the whole filtered set. */
    selection?: DataTableSelection<T>;
    /** Integrated pagination. `pageSizeOptions` overrides the default choices
     *  of the `Pagination` control; give it whenever `pageSize` is not one of
     *  them, or the select shows a size the table does not use. The default
     *  page size is 100. */
    pagination?: { pageSize?: number; pageSizeOptions?: readonly number[] } | boolean;
    /** The copy when `data` is empty. The table then shows no header: it shows
     *  this copy in a centred block. A plain string becomes an `EmptyState`
     *  title. */
    empty: ReactNode;
    loading?: boolean;
    ariaLabel: string;
    /** false = no filter funnels anywhere. Set it to false when `data` is not the
     *  full set, for example a server page or a capped list, because a filter
     *  can only see the rows it holds. The default is true. */
    filterable?: boolean;
    /** Uncontrolled: the filters the table opens with. Ignored when `filters`
     *  is supplied. */
    initialFilters?: ColumnFilterModel;
    /** Controlled filters, keyed by column id. Pair it with `onFiltersChange`.
     *  Use it to survive a remount, see `useTableViewState`. */
    filters?: ColumnFilterModel;
    /** Fires on every filter change, in both controlled and uncontrolled mode. */
    onFiltersChange?: (next: ColumnFilterModel) => void;
    /** The filters reset when this value changes, for example an IM book id. */
    filterScopeKey?: string;
    /** The row height. `default` is 26px, `tight` is 24px, and `logo` is 32px
     *  for rows that show a logo. A coarse pointer raises each row to 32px. */
    rowHeight?: DataTableRowHeight;
    /** The column chooser at the right end of the header. The default is on
     *  when the table has four columns or more, or a `defaultHidden` column. */
    columnChooser?: boolean;
    /** The key under which this browser keeps the chosen columns. The default
     *  is the route and `ariaLabel`, with ids and numbers taken out. Give a key
     *  when two different tables on one page share a label. */
    columnsKey?: string;
    /** Gives the view of the table to the page, for an export of the view.
     *  See `exportTableView`. */
    viewRef?: Ref<DataTableViewHandle>;
    className?: string;
}

export type DataTableRowHeight = 'default' | 'tight' | 'logo';

const ROW_HEIGHT: Record<DataTableRowHeight, string> = {
    default: 'h-[26px] pointer-coarse:h-8',
    tight: 'h-6 pointer-coarse:h-8',
    logo: 'h-8',
};

/** The page size when `pagination` gives none. */
export const DEFAULT_PAGE_SIZE = 100;

const ALIGN: Record<'left' | 'right' | 'center', string> = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
};

function resolveAlign<T>(col: DataTableColumn<T>): 'left' | 'right' | 'center' {
    if (col.align) return col.align;
    return col.numeric ? 'right' : 'left';
}

const MIN_COLUMN_PX = 48;
const DEFAULT_COLUMN_PX = 150;
// The chooser column at the right end of the header (w-6).
const CHOOSER_COLUMN_PX = 24;

// The column layout of one table: the columns, their order and their widths.
// It lives in the browser of the reader only, under a key for each user: a
// view preference. Storage can be missing or can throw (a private window,
// blocked site data), so each access has a fallback, and the table then shows
// its default layout.
function useColumnPrefs(
    tableKey: string,
    userId: string | null,
): [ColumnPrefs, (next: ColumnPrefs) => void] {
    const storageKey = columnPrefsStorageKey(tableKey, userId);
    // Before SEP-298 the key held no user. The user reads that record until
    // the first change writes a record of their own.
    const browserKey = columnPrefsStorageKey(tableKey);
    const read = () => {
        if (typeof window === 'undefined') return EMPTY_COLUMN_PREFS;
        try {
            const own = window.localStorage.getItem(storageKey);
            if (own !== null || !userId) return parseColumnPrefs(own);
            return parseColumnPrefs(window.localStorage.getItem(browserKey));
        } catch {
            return EMPTY_COLUMN_PREFS;
        }
    };
    const [state, setState] = useState(() => ({ key: storageKey, prefs: read() }));
    // A new table key reads its own preference.
    if (state.key !== storageKey) setState({ key: storageKey, prefs: read() });
    const write = useCallback(
        (next: ColumnPrefs) => {
            setState({ key: storageKey, prefs: next });
            try {
                // A reset by a user keeps an empty record, so the record of
                // the browser does not come back.
                if (isEmptyColumnPrefs(next) && !userId) {
                    window.localStorage.removeItem(storageKey);
                } else {
                    window.localStorage.setItem(storageKey, serialiseColumnPrefs(next));
                }
            } catch {
                // The choice still applies until the page closes.
            }
        },
        [storageKey, userId],
    );
    return [state.prefs, write];
}

// Compact is the only density. Cells have 6px side padding and no vertical
// padding. Rows have horizontal lines only, with a faint zebra fill. There
// are no vertical gridlines.
const CELL_PADDING = 'px-1.5 py-0';
const HEADER_PADDING = 'h-6 px-1.5 py-0';
const ZEBRA = 'even:bg-[rgb(var(--color-row-hover)/0.02)]';
// A non-replaced inline box (a bare `<span>`) ignores `overflow`/`text-overflow`
// entirely — only `white-space:nowrap` from Tailwind's `truncate` has any
// effect on it, so it refuses to wrap *and* refuses to clip, painting straight
// over neighbouring cells (SEP-170). `overflow-hidden` on the body `<td>` is
// the backstop: it clips any cell content that overflows its fixed column
// width regardless of what the column author remembered to put on their own
// span. It does not by itself produce an ellipsis — a column still needs
// `block truncate` (+ `title=`) on its own text node for that — but it
// guarantees a page can no longer bleed into its neighbours. Checked against
// every `DataTableColumn` consumer for cell content that relies on visible
// overflow (dropdown/popover panels, focus rings, multi-line clamps): none do.
// Note the two escape hatches this deliberately does NOT clip: `Modal` portals
// out to `<body>`, and the expandable-row `<td colSpan>` below is rendered
// without this class. A future cell needing to paint outside its column should
// use one of those rather than removing this.
//
// `text-ellipsis` on the same `<td>` gives the ellipsis for inline text that
// does not wrap (a bare `<span className="truncate">`): the cell is the block
// container of that text, so the cell draws the "…". It does not change a cell
// that fits, and it does not make a cell wrap or stop wrapping.
const CELL_OVERFLOW = 'overflow-hidden text-ellipsis';

// Loading state: render this many placeholder rows so the table reads as a
// populated surface mid-fetch rather than a single "Loading…" line.
const SKELETON_ROW_COUNT = 8;
// A rotating palette of bone widths so skeleton cells look like real, varied
// content instead of a uniform grid. Indexed by (rowIndex + columnIndex).
const SKELETON_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-2/5', 'w-1/2'];
// Default starting-width cap for a column that declares no explicit `width`:
// content-sized columns start no wider than this so a long free-text column
// doesn't blow the table out. Double-clicking the resize handle auto-fits the
// column to its full content (Excel-style), past this cap.
const DEFAULT_MAX_COLUMN_PX = 280;

// How many distinct values the filter panel reads for one column. The panel
// shows the first 500 of the search results. Search reaches the rest, up to
// this cap.
const VALUE_LIST_SEARCH_CAP = 5000;

// The plain text of a column header, for a label or a chip. A header that is
// not text falls back to the column id.
function headerLabel<T>(col: DataTableColumn<T>): string {
    if (typeof col.header === 'string') return col.header;
    if (typeof col.header === 'number') return String(col.header);
    return col.id;
}

// Parse a CSS width string (e.g. "200px", "200") to a pixel number; non-px
// units / unset return undefined (no declared cap).
function parsePx(value?: string): number | undefined {
    if (!value) return undefined;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : undefined;
}

// The single table mechanism for the kit. TanStack Table headless under kit
// styling, rendered as a semantic <table> inside an overflow-x-auto wrapper.
// Client sorting (with indicators), optional integrated Pagination, expandable
// rows, row click/selection, loading + empty states.
export default function DataTable<T>({
    columns,
    data,
    rowKey,
    onRowClick,
    rowClassName,
    selectedKey,
    initialSort,
    sort,
    onSortChange,
    stickyHeader = true,
    expandable,
    selection,
    pagination = false,
    empty,
    loading = false,
    ariaLabel,
    filterable = true,
    initialFilters,
    filters,
    onFiltersChange,
    filterScopeKey,
    rowHeight: rowHeightProp = 'default',
    columnChooser,
    columnsKey,
    viewRef,
    className,
}: DataTableProps<T>) {
    // ---- Column chooser ---------------------------------------------------
    // `columns` is every column the table can show. `visibleColumns` is what
    // the reader chose. A hidden column keeps its sort and its filter: the
    // filter chip still shows, so the reader can see why rows are missing.
    const chooserOn =
        columnChooser ?? (columns.length >= 4 || columns.some((c) => c.defaultHidden));
    const userId = useSignedInUserId();
    const [storedPrefs, setColumnPrefs] = useColumnPrefs(
        columnsKey ??
            defaultColumnsKey(
                typeof window === 'undefined' ? '' : window.location.pathname,
                ariaLabel,
            ),
        userId,
    );
    // With the chooser off the reader has no way to reset a layout, so the
    // table keeps its default layout.
    const columnPrefs = chooserOn ? storedPrefs : EMPTY_COLUMN_PREFS;
    // A column with no header text (a row action, a status dot) has no name to
    // choose by. It always shows, it never moves, and the chooser does not
    // list it.
    const chooserColumns = useMemo(
        () =>
            columns.map((c) => {
                const nameless = headerLabel(c).trim() === '';
                return {
                    id: c.id,
                    defaultHidden: c.defaultHidden,
                    hideable: c.hideable === false || nameless ? false : undefined,
                    movable: c.reorderable === false || nameless ? false : undefined,
                };
            }),
        [columns],
    );
    // With the chooser off the reader could never add a hidden column, so
    // every column shows.
    const visibleIdList = chooserOn
        ? visibleColumnIds(chooserColumns, columnPrefs)
        : columns.map((c) => c.id);
    const visibleSig = visibleIdList.join('|');
    // In the order of the reader.
    const visibleColumns = useMemo(() => {
        const byId = new Map(columns.map((c) => [c.id, c]));
        return visibleSig
            .split('|')
            .map((id) => byId.get(id))
            .filter((c): c is DataTableColumn<T> => c !== undefined);
    }, [columns, visibleSig]);
    // With no data at all, the table shows the empty copy and no header. A
    // filter that hides each row keeps the header, so the reader can clear it.
    const bare = !loading && data.length === 0;
    const frameRef = useRef<HTMLDivElement>(null);
    const bareHeight = useFillToBottom(frameRef, bare);
    // The column that takes the spare width: the one that sets `grow`, else the
    // first left-aligned column with a header.
    const growId = useMemo(() => {
        const named = visibleColumns.filter((c) => headerLabel(c).trim() !== '');
        const chosen =
            named.find((c) => c.grow === true) ??
            named.find((c) => c.grow !== false && resolveAlign(c) === 'left');
        return chosen?.id;
    }, [visibleColumns]);
    // The columns that show, without their order. A move does not measure the
    // columns again.
    const measureSig = [...visibleIdList].sort().join('|');
    const columnVisibility = useMemo<VisibilityState>(() => {
        const ids = new Set(visibleSig.split('|'));
        const map: VisibilityState = {};
        for (const c of columns) if (!ids.has(c.id)) map[c.id] = false;
        return map;
    }, [columns, visibleSig]);
    const [chooserOpen, setChooserOpen] = useState(false);
    const closeChooser = useCallback(() => setChooserOpen(false), []);
    const chooserCellRef = useRef<HTMLTableCellElement>(null);
    const movableIds = new Set(
        chooserOn ? chooserColumns.filter((c) => c.movable !== false).map((c) => c.id) : [],
    );
    // Pointer reorder: the header that the reader drags, and the header under it.
    const [dragColId, setDragColId] = useState<string | null>(null);
    const [dropColId, setDropColId] = useState<string | null>(null);
    // True from a press on a resize handle until the release. A drag of the
    // header must not start then.
    const resizePressRef = useRef(false);

    // Sort is controlled when the `sort` prop is present at all (`null` is a
    // meaningful controlled value: "unsorted"). Otherwise the table owns it,
    // exactly as before, seeded once from `initialSort`.
    const isSortControlled = sort !== undefined;
    const [internalSorting, setInternalSorting] = useState<SortingState>(
        initialSort ? [{ id: initialSort.id, desc: initialSort.desc }] : [],
    );
    // Keyed on the primitives, not the prop object: a caller that rebuilds
    // `{ id, desc }` every render (a URL-derived sort does) must not hand
    // TanStack a fresh `sorting` array and defeat its sorted-row memo.
    const controlledSortId = sort?.id ?? null;
    const controlledSortDesc = sort?.desc ?? false;
    const controlledSorting = useMemo<SortingState>(
        () =>
            controlledSortId === null ? [] : [{ id: controlledSortId, desc: controlledSortDesc }],
        [controlledSortId, controlledSortDesc],
    );
    const sorting = isSortControlled ? controlledSorting : internalSorting;

    const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
        const next = typeof updater === 'function' ? updater(sorting) : updater;
        const head = next[0];
        onSortChange?.(head ? { id: head.id, desc: head.desc } : null);
        if (!isSortControlled) setInternalSorting(next);
    };

    // ---- Column filters ---------------------------------------------------
    // Filters follow the same ownership rule as sort: controlled when the
    // `filters` prop is present at all, otherwise owned by the table and seeded
    // once from `initialFilters`.
    const columnIdsSig = columns.map((c) => c.id).join('|');
    const isFilterControlled = filters !== undefined;
    const [internalFilters, setInternalFilters] = useState<ColumnFilterModel>(initialFilters ?? {});
    // Keyed on the serialised model, not on the prop object: a caller that
    // rebuilds the model every render (a URL-derived one does) must not hand
    // the pipeline a fresh identity and defeat the memos below.
    const controlledFiltersSig = filters ? serialiseModel(filters) : '';
    const controlledFilters = useMemo<ColumnFilterModel>(
        () => filters ?? {},
        // The signature stands for the content of `filters`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [controlledFiltersSig],
    );
    const filterModel = isFilterControlled ? controlledFilters : internalFilters;

    // Reset the filters when the scope or the column set changes. The
    // uncontrolled state adjusts during render, which is React's documented way
    // to derive state from a changed input. A controlled model belongs to the
    // caller, so both modes tell the caller through the callback, in an effect.
    const filterResetKey = `${filterScopeKey ?? ''}\u0000${columnIdsSig}`;
    const [seenResetKey, setSeenResetKey] = useState(filterResetKey);
    if (seenResetKey !== filterResetKey) {
        setSeenResetKey(filterResetKey);
        if (!isFilterControlled && Object.keys(internalFilters).length > 0) {
            setInternalFilters({});
        }
    }
    // Holds the model of the previous render, so the reset effect can see what
    // the reset removed.
    const previousModelRef = useRef(filterModel);
    const appliedResetKeyRef = useRef(filterResetKey);
    useEffect(() => {
        if (appliedResetKeyRef.current !== filterResetKey) {
            appliedResetKeyRef.current = filterResetKey;
            if (Object.keys(previousModelRef.current).length > 0) onFiltersChange?.({});
        }
        previousModelRef.current = filterModel;
    });

    const [page, setPage] = useState(1);
    const changeFilters = (next: ColumnFilterModel) => {
        if (!isFilterControlled) setInternalFilters(next);
        onFiltersChange?.(next);
        setPage(1);
    };
    const setColumnFilter = (id: string, next: ColumnFilter | undefined) => {
        const model = { ...filterModel };
        if (next === undefined) delete model[id];
        else model[id] = next;
        changeFilters(model);
    };
    const [openFilterId, setOpenFilterId] = useState<string | null>(null);
    const closeFilter = useCallback(() => setOpenFilterId(null), []);

    // The resolved filter of each column. A column with no spec has no funnel.
    const filterSpecs = useMemo(() => {
        const map = new Map<string, ResolvedFilterSpec<T>>();
        if (!filterable) return map;
        for (const col of columns) {
            const spec = resolveFilterSpec(col, data);
            if (spec) map.set(col.id, spec);
        }
        return map;
    }, [filterable, columns, data]);
    const activeFilterIds = Object.keys(filterModel).filter((id) => filterSpecs.has(id));
    const hasActiveFilters = activeFilterIds.length > 0;
    const filteredData = useMemo(
        () =>
            hasActiveFilters
                ? applyColumnFilters(data, filterModel, filterSpecs, clockNow())
                : data,
        [hasActiveFilters, data, filterModel, filterSpecs],
    );

    // `pagination` alone (true) takes the default page size.
    const paginationOptions = typeof pagination === 'object' ? pagination : {};
    const [pageSize, setPageSize] = useState(
        pagination ? (paginationOptions.pageSize ?? DEFAULT_PAGE_SIZE) : data.length,
    );
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    // Column widths are resizable (TanStack column sizing) and start at
    // min(declared width, the width the longest cell actually needs). The
    // starting width is derived from a one-shot content-measuring pass: render
    // an auto-layout table so each column hugs its widest cell, read those
    // widths, cap them at the declared `width`, then lock in a fixed layout.
    //
    // Sizing is left *uncontrolled* (owned by the table's own state) so the
    // live-resize handler — which writes column sizing from inside its column
    // sizing-info updater — lands in the same store; a separately-controlled
    // useState would drop that nested cross-hook update. Measured widths are
    // pushed imperatively via `table.setColumnSizing` from the effect below.
    const [measuring, setMeasuring] = useState(true);
    // Set by double-clicking a resize handle: the next measuring pass auto-fits
    // just this column to its full content width, then clears.
    const [autoFitColId, setAutoFitColId] = useState<string | null>(null);
    const headerRefs = useRef(new Map<string, HTMLTableCellElement>());
    const funnelRefs = useRef(new Map<string, HTMLButtonElement>());
    const measuredSigRef = useRef<string | null>(null);

    // Horizontal-scroll affordance: fade the left/right edge only when content
    // is actually hidden in that direction, so it's clear the table scrolls.
    const scrollRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const [edges, setEdges] = useState({ left: false, right: false });

    const updateEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setEdges({
            left: el.scrollLeft > 1,
            right: el.scrollLeft < maxScroll - 1,
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateEdges();
        el.addEventListener('scroll', updateEdges, { passive: true });
        // Re-measure when the viewport (container) or the table content width
        // changes — covers resize, sorting, pagination, and row expansion.
        const ro = new ResizeObserver(updateEdges);
        ro.observe(el);
        if (tableRef.current) ro.observe(tableRef.current);
        return () => {
            el.removeEventListener('scroll', updateEdges);
            ro.disconnect();
        };
    }, [updateEdges, bare]);

    const tableColumns = useMemo<ColumnDef<T>[]>(
        () =>
            columns.map((col) => ({
                id: col.id,
                // accessorFn drives sorting only; cell rendering is done directly
                // from the kit column's `cell` callback below (not via TanStack).
                // Coerce null → undefined so `sortUndefined: 'last'` sinks missing
                // values to the bottom in *both* sort directions.
                accessorFn: col.sortValue
                    ? (row: T) => col.sortValue!(row) ?? undefined
                    : undefined,
                enableSorting: Boolean(col.sortable && col.sortValue),
                // A numeric column opens on the largest figure: "highest to
                // lowest" is what a reader wants first from a quantity, and it
                // makes the two states read as one plain pair. A text column
                // still opens A-Z.
                sortDescFirst: Boolean(col.numeric),
                sortUndefined: 'last',
                size: parsePx(col.width) ?? parsePx(col.minWidth) ?? DEFAULT_COLUMN_PX,
                minSize: Math.min(MIN_COLUMN_PX, parsePx(col.width) ?? MIN_COLUMN_PX),
            })),
        [columns],
    );

    const table = useReactTable({
        data: filteredData,
        columns: tableColumns,
        state: { sorting, columnVisibility },
        onSortingChange: handleSortingChange,
        getRowId: (row) => rowKey(row),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        // TWO SORT STATES, NOT THREE. The third state (back to the unsorted
        // source order) looked identical to a sort the reader had not chosen,
        // so a header click cycled through a state nobody could name. A header
        // now only ever means ascending or descending, and a numeric column
        // opens on the biggest figure (`sortDescFirst` below).
        enableSortingRemoval: false,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
    });

    const sortedRows = table.getRowModel().rows;
    const total = sortedRows.length;

    useImperativeHandle(
        viewRef,
        () => ({
            getView: () =>
                buildTableView(
                    visibleColumns.filter((c) => headerLabel(c).trim() !== ''),
                    sortedRows.map((r) => r.original),
                    headerLabel,
                ),
        }),
        [visibleColumns, sortedRows],
    );

    // Measure → size pass. `measuring` toggles the table into auto layout (each
    // column hugs its content); this layout effect then reads the header cell
    // widths (= the widest cell in that column, header included). It serves two
    // jobs, both pre-paint so the auto-layout frame never shows:
    //   - initial sizing (once per distinct column set): start each column at
    //     min(declared `width` ?? default cap, content), floored at the min;
    //   - double-click auto-fit (`autoFitColId`): size just that column to its
    //     full content width, uncapped, floored at the min.
    useLayoutEffect(() => {
        if (total === 0) {
            // No body rows to content-measure (loading or empty). Leave the
            // auto-layout measuring pass so the header row — and the loading
            // skeleton rows — lay out fixed + min-w-full and fill the
            // container, rather than the table hugging its header text. The
            // signature is deliberately left unrecorded so real data triggers
            // a proper content-measuring pass once it arrives.
            if (measuring) setMeasuring(false);
            return;
        }
        const needInitial = measuredSigRef.current !== measureSig;
        const needAutoFit = autoFitColId !== null;
        if (!needInitial && !needAutoFit) return;
        if (!measuring) {
            setMeasuring(true);
            return;
        }
        const measure = (id: string) => {
            const el = headerRefs.current.get(id);
            return el ? Math.ceil(el.getBoundingClientRect().width) : 0;
        };
        if (needAutoFit) {
            const content = measure(autoFitColId);
            const fitted = Math.max(MIN_COLUMN_PX, content || MIN_COLUMN_PX);
            table.setColumnSizing((old) => ({ ...old, [autoFitColId]: fitted }));
            if (chooserOn) setColumnPrefs(setColumnWidth(columnPrefs, autoFitColId, fitted));
            setAutoFitColId(null);
            setMeasuring(false);
            return;
        }
        // A hidden column keeps the width it had. A column that the reader
        // adds is measured with the others.
        const next: ColumnSizingState = { ...table.getState().columnSizing };
        for (const col of visibleColumns) {
            // A width that the reader set wins over the measured width.
            const stored = storedColumnWidth(columnPrefs, col.id);
            if (stored !== undefined) {
                next[col.id] = stored;
                continue;
            }
            const content = measure(col.id);
            const cap = parsePx(col.width) ?? DEFAULT_MAX_COLUMN_PX;
            // The floor never passes a declared width: a 32px status column
            // stays 32px.
            const floor = Math.min(MIN_COLUMN_PX, cap);
            next[col.id] =
                content > 0
                    ? Math.max(floor, Math.min(cap, content))
                    : Math.max(parsePx(col.minWidth) ?? floor, cap);
        }
        measuredSigRef.current = measureSig;
        table.setColumnSizing(next);
        setMeasuring(false);
        // `columnPrefs` is read, not watched: a stored width applies at the next
        // measure, and a resize already shows the width that it stores.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measureSig, total, measuring, columns, visibleColumns, table, autoFitColId]);

    // Keep the width of a column when a resize ends.
    const resizingColId = table.getState().columnSizingInfo.isResizingColumn;
    const lastResizingRef = useRef<string | false>(false);
    useEffect(() => {
        const ended = lastResizingRef.current;
        lastResizingRef.current = resizingColId;
        if (resizingColId || !ended || !chooserOn) return;
        const width = table.getColumn(ended)?.getSize();
        if (width !== undefined) setColumnPrefs(setColumnWidth(columnPrefs, ended, width));
    }, [resizingColId, chooserOn, table, columnPrefs, setColumnPrefs]);

    // A `grow` column has no fixed width, so the fixed layout gives it the
    // spare width of the table. After a resize by the reader it has a fixed
    // width again, so the drag shows.
    const [resizedIds, setResizedIds] = useState<ReadonlySet<string>>(() => new Set());
    useEffect(() => {
        if (resizingColId && !resizedIds.has(resizingColId)) {
            setResizedIds((prev) => new Set(prev).add(resizingColId));
        }
    }, [resizingColId, resizedIds]);
    const growsFree = (id: string) =>
        id === growId && !resizedIds.has(id) && storedColumnWidth(columnPrefs, id) === undefined;

    // A reset gives each column its measured width again.
    const resetLayout = () => {
        setColumnPrefs(EMPTY_COLUMN_PREFS);
        setResizedIds(new Set());
        measuredSigRef.current = null;
        setMeasuring(true);
    };

    const moveTo = (id: string, targetId: string) =>
        setColumnPrefs(moveColumn(chooserColumns, columnPrefs, id, targetId));
    const moveBy = (id: string, step: -1 | 1) =>
        setColumnPrefs(moveColumnBy(chooserColumns, columnPrefs, id, step));

    useEffect(() => {
        if (!dragColId) return;
        const clear = () => {
            setDragColId(null);
            setDropColId(null);
        };
        document.addEventListener('dragend', clear);
        return () => document.removeEventListener('dragend', clear);
    }, [dragColId]);
    useEffect(() => {
        const release = () => {
            resizePressRef.current = false;
        };
        document.addEventListener('mouseup', release);
        document.addEventListener('touchend', release);
        return () => {
            document.removeEventListener('mouseup', release);
            document.removeEventListener('touchend', release);
        };
    }, []);

    // Clamp the current page into range so a shrinking `data` (filter applied,
    // rows removed) never strands the view on an empty page past the end.
    const pageCount = pagination ? Math.max(1, Math.ceil(total / pageSize)) : 1;
    const safePage = Math.min(page, pageCount);

    const paginated = pagination
        ? sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize)
        : sortedRows;

    const rowHeight = ROW_HEIGHT[rowHeightProp];
    const colCount =
        visibleColumns.length + (expandable ? 1 : 0) + (selection ? 1 : 0) + (chooserOn ? 1 : 0);

    // The header checkbox counts every selectable row that passes the filters,
    // not the rows on the current page: an operator who filters to
    // "Unassigned" and ticks the header means the whole filtered set.
    const selectableKeys = selection
        ? filteredData.filter((r) => selection.isSelectable?.(r) ?? true).map(rowKey)
        : [];
    // Rows that are selected and that a filter hides. They stay selected.
    let hiddenSelected = 0;
    if (selection && hasActiveFilters) {
        const visible = new Set(filteredData.map(rowKey));
        for (const r of data) {
            const k = rowKey(r);
            if (selection.selectedKeys.has(k) && !visible.has(k)) hiddenSelected += 1;
        }
    }
    const selectedCount = selectableKeys.filter((k) => selection?.selectedKeys.has(k)).length;
    const allSelected = selectableKeys.length > 0 && selectedCount === selectableKeys.length;
    const someSelected = selectedCount > 0 && !allSelected;
    // The leaf header group drives per-column resize handlers. Total width is
    // the sum of column sizes plus the fixed expandable-chevron column (w-9).
    const headerGroup = table.getHeaderGroups()[0];
    const totalWidth =
        table.getTotalSize() + (expandable ? 36 : 0) + (chooserOn ? CHOOSER_COLUMN_PX : 0);
    const chooserChanged = chooserOn && !isDefaultColumns(chooserColumns, columnPrefs);

    // The open filter panel. The value list of a column ignores that column's
    // own filter, so the reader can widen it again, as in Excel.
    const openColumn = openFilterId ? columns.find((c) => c.id === openFilterId) : undefined;
    const openSpec = openColumn ? filterSpecs.get(openColumn.id) : undefined;
    const openAnchor = openColumn ? headerRefs.current.get(openColumn.id) : undefined;
    const openDistinct = useMemo(
        () =>
            openColumn && openSpec
                ? distinctValues(
                      applyColumnFilters(data, filterModel, filterSpecs, clockNow(), openColumn.id),
                      openSpec,
                      VALUE_LIST_SEARCH_CAP,
                  )
                : { entries: [], blanks: 0, truncated: false },
        [openColumn, openSpec, data, filterModel, filterSpecs],
    );
    const openSort =
        openColumn && sorting[0]?.id === openColumn.id ? (sorting[0].desc ? 'desc' : 'asc') : null;

    function toggleExpand(key: string) {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    return (
        <div
            ref={frameRef}
            // An empty table that ends the page fills it down to the bottom.
            style={bareHeight === undefined ? undefined : { minHeight: bareHeight }}
            className={cn(
                'septima-table overflow-hidden rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))]',
                bare && 'flex flex-col',
                className,
            )}
        >
            {hasActiveFilters && (
                <DataTableFilterBar
                    chips={activeFilterIds.map((id) => {
                        const col = columns.find((c) => c.id === id);
                        return {
                            id,
                            label: col ? headerLabel(col) : id,
                            description: describeFilter(filterModel[id], filterSpecs.get(id)?.type),
                        };
                    })}
                    shown={total}
                    total={data.length}
                    hiddenSelected={hiddenSelected}
                    onOpen={setOpenFilterId}
                    onRemove={(id) => setColumnFilter(id, undefined)}
                    onClear={() => changeFilters({})}
                />
            )}
            {bare ? (
                <div className="septima-table-bare flex-1">
                    {typeof empty === 'string' ? <EmptyState title={empty} /> : empty}
                </div>
            ) : (
                <div className="relative">
                    <div ref={scrollRef} className="scrollbar-none overflow-x-auto">
                        <table
                            ref={tableRef}
                            aria-label={ariaLabel}
                            style={measuring ? undefined : { width: totalWidth }}
                            className={cn(
                                'border-collapse text-table-body text-[rgb(var(--color-text-primary))]',
                                measuring ? 'table-auto w-max' : 'table-fixed min-w-full',
                            )}
                        >
                            <thead
                                className={cn(
                                    'bg-[rgb(var(--color-surface-secondary))]',
                                    stickyHeader && 'sticky top-0 z-10',
                                )}
                            >
                                <tr className="border-b border-[rgb(var(--color-divider))]">
                                    {selection && (
                                        <th className="w-9 px-2 text-center align-middle leading-none">
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={() =>
                                                    selection.onToggleAll(selectableKeys)
                                                }
                                                aria-label={
                                                    selection.allLabel ?? 'Select every row'
                                                }
                                                disabled={selectableKeys.length === 0}
                                            />
                                        </th>
                                    )}
                                    {expandable && <th className="w-9" aria-hidden="true" />}
                                    {visibleColumns.map((col) => {
                                        const tcol = table.getColumn(col.id);
                                        const header = headerGroup?.headers.find(
                                            (h) => h.column.id === col.id,
                                        );
                                        const canSort = Boolean(col.sortable && col.sortValue);
                                        const sortDir = tcol?.getIsSorted();
                                        const filterSpec = filterSpecs.get(col.id);
                                        const activeFilter = filterSpec
                                            ? filterModel[col.id]
                                            : undefined;
                                        const isFilterOpen = openFilterId === col.id;
                                        const colLabel = headerLabel(col);
                                        const filterDescription = activeFilter
                                            ? describeFilter(activeFilter, filterSpec?.type)
                                            : undefined;
                                        const movable = !measuring && movableIds.has(col.id);
                                        const headerAlign = resolveAlign(col);
                                        const dropSide =
                                            dropColId === col.id &&
                                            dragColId &&
                                            dragColId !== col.id
                                                ? visibleIdList.indexOf(dragColId) <
                                                  visibleIdList.indexOf(col.id)
                                                    ? 'right'
                                                    : 'left'
                                                : null;
                                        const onHeaderKeyDown = (e: {
                                            altKey: boolean;
                                            shiftKey: boolean;
                                            key: string;
                                            preventDefault: () => void;
                                        }) => {
                                            if (
                                                !movableIds.has(col.id) ||
                                                !e.altKey ||
                                                !e.shiftKey
                                            ) {
                                                return false;
                                            }
                                            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                return false;
                                            }
                                            e.preventDefault();
                                            moveBy(col.id, e.key === 'ArrowLeft' ? -1 : 1);
                                            return true;
                                        };
                                        return (
                                            <th
                                                key={col.id}
                                                draggable={movable || undefined}
                                                data-column-id={col.id}
                                                onDragStart={
                                                    movable
                                                        ? (
                                                              e: ReactDragEvent<HTMLTableCellElement>,
                                                          ) => {
                                                              if (resizePressRef.current) {
                                                                  e.preventDefault();
                                                                  return;
                                                              }
                                                              e.dataTransfer.effectAllowed = 'move';
                                                              e.dataTransfer.setData(
                                                                  'text/plain',
                                                                  colLabel,
                                                              );
                                                              setDragColId(col.id);
                                                          }
                                                        : undefined
                                                }
                                                onDragOver={
                                                    dragColId && movable
                                                        ? (e) => {
                                                              e.preventDefault();
                                                              e.dataTransfer.dropEffect = 'move';
                                                              if (dropColId !== col.id)
                                                                  setDropColId(col.id);
                                                          }
                                                        : undefined
                                                }
                                                onDrop={
                                                    dragColId && movable
                                                        ? (e) => {
                                                              e.preventDefault();
                                                              moveTo(dragColId, col.id);
                                                              setDragColId(null);
                                                              setDropColId(null);
                                                          }
                                                        : undefined
                                                }
                                                ref={(el) => {
                                                    if (el) headerRefs.current.set(col.id, el);
                                                    else headerRefs.current.delete(col.id);
                                                }}
                                                scope="col"
                                                aria-sort={
                                                    canSort
                                                        ? sortDir === 'asc'
                                                            ? 'ascending'
                                                            : sortDir === 'desc'
                                                              ? 'descending'
                                                              : 'none'
                                                        : undefined
                                                }
                                                style={
                                                    measuring || growsFree(col.id)
                                                        ? undefined
                                                        : { width: tcol?.getSize() }
                                                }
                                                className={cn(
                                                    // A header has the alignment of its
                                                    // cells, so a figure sits under its
                                                    // name (SEP-295).
                                                    'group/th relative text-table-header text-[rgb(var(--color-text-secondary))]',
                                                    ALIGN[headerAlign],
                                                    HEADER_PADDING,
                                                    measuring && 'whitespace-nowrap',
                                                    movable && 'cursor-grab active:cursor-grabbing',
                                                    dragColId === col.id && 'opacity-50',
                                                    // The drop line shows where the column lands.
                                                    dropSide === 'left' &&
                                                        'shadow-[inset_2px_0_0_rgb(var(--color-accent))]',
                                                    dropSide === 'right' &&
                                                        'shadow-[inset_-2px_0_0_rgb(var(--color-accent))]',
                                                )}
                                            >
                                                <div
                                                    className={cn(
                                                        'flex items-center gap-1',
                                                        headerAlign === 'right' && 'justify-end',
                                                        headerAlign === 'center' &&
                                                            'justify-center',
                                                        activeFilter &&
                                                            (headerAlign === 'right'
                                                                ? 'pl-6'
                                                                : 'pr-6'),
                                                    )}
                                                >
                                                    {canSort ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => tcol?.toggleSorting()}
                                                            aria-keyshortcuts={
                                                                movableIds.has(col.id)
                                                                    ? 'Alt+Shift+ArrowLeft Alt+Shift+ArrowRight'
                                                                    : undefined
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (onHeaderKeyDown(e)) return;
                                                                if (
                                                                    e.altKey &&
                                                                    e.key === 'ArrowDown' &&
                                                                    filterSpec
                                                                ) {
                                                                    e.preventDefault();
                                                                    setOpenFilterId(col.id);
                                                                }
                                                            }}
                                                            className={cn(
                                                                'inline-flex items-center gap-1 select-none',
                                                                // The sort arrow of a right-aligned
                                                                // header goes before the name. The
                                                                // name then ends on the edge of the
                                                                // figures, and a sort does not move it.
                                                                headerAlign === 'right' &&
                                                                    'flex-row-reverse',
                                                                'hover:text-[rgb(var(--color-text-primary))]',
                                                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)] rounded',
                                                            )}
                                                        >
                                                            <span>{col.header}</span>
                                                            <span className="flex h-3 w-3 items-center justify-center">
                                                                {sortDir === 'asc' ? (
                                                                    <ChevronUp className="h-3 w-3" />
                                                                ) : sortDir === 'desc' ? (
                                                                    <ChevronDown className="h-3 w-3" />
                                                                ) : null}
                                                            </span>
                                                        </button>
                                                    ) : (
                                                        col.header
                                                    )}
                                                    {filterSpec && (
                                                        // The funnel sits over the side padding of the
                                                        // header (the left side for a right-aligned
                                                        // header), so it adds no width. A column that
                                                        // is filtered reserves its width (`pr-6` or
                                                        // `pl-6`), so the header text never sits under it.
                                                        <button
                                                            type="button"
                                                            ref={(el) => {
                                                                if (el)
                                                                    funnelRefs.current.set(
                                                                        col.id,
                                                                        el,
                                                                    );
                                                                else
                                                                    funnelRefs.current.delete(
                                                                        col.id,
                                                                    );
                                                            }}
                                                            data-column-filter-trigger=""
                                                            aria-haspopup="dialog"
                                                            aria-expanded={isFilterOpen}
                                                            aria-label={
                                                                filterDescription
                                                                    ? `Filter ${colLabel}, active: ${filterDescription}`
                                                                    : `Filter ${colLabel}`
                                                            }
                                                            title={filterDescription}
                                                            onClick={() =>
                                                                setOpenFilterId(
                                                                    isFilterOpen ? null : col.id,
                                                                )
                                                            }
                                                            className={cn(
                                                                'absolute top-1/2 grid h-4 w-4 -translate-y-1/2 place-items-center rounded-[3px]',
                                                                // A right-aligned name ends at the
                                                                // right padding, so the funnel goes
                                                                // over the left padding.
                                                                headerAlign === 'right'
                                                                    ? 'left-1.5'
                                                                    : 'right-2',
                                                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                                                                activeFilter
                                                                    ? 'opacity-100 text-[rgb(var(--color-accent))] bg-[rgb(var(--color-accent)/0.12)]'
                                                                    : cn(
                                                                          'bg-[rgb(var(--color-surface-secondary))] text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-surface-hover)/0.06)]',
                                                                          'opacity-0 group-hover/th:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100',
                                                                          isFilterOpen &&
                                                                              'opacity-100',
                                                                      ),
                                                            )}
                                                        >
                                                            <Filter
                                                                className={cn(
                                                                    'h-3 w-3',
                                                                    activeFilter && 'fill-current',
                                                                )}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                                {!measuring && header && (
                                                    <span
                                                        role="separator"
                                                        aria-orientation="vertical"
                                                        title="Drag to resize · double-click to fit to content"
                                                        onMouseDown={(e) => {
                                                            resizePressRef.current = true;
                                                            header.getResizeHandler()(e);
                                                        }}
                                                        onTouchStart={(e) => {
                                                            resizePressRef.current = true;
                                                            header.getResizeHandler()(e);
                                                        }}
                                                        draggable={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onDoubleClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            setAutoFitColId(col.id);
                                                        }}
                                                        className="group/resize absolute inset-y-0 right-0 z-10 flex w-2 cursor-col-resize touch-none select-none justify-center"
                                                    >
                                                        <span
                                                            className={cn(
                                                                'h-1/2 w-px self-center bg-[rgb(var(--color-divider))] opacity-0 transition-opacity duration-card-hover',
                                                                'group-hover/resize:opacity-100',
                                                                header.column.getIsResizing() &&
                                                                    'opacity-100 bg-[rgb(var(--color-accent))]',
                                                            )}
                                                        />
                                                    </span>
                                                )}
                                            </th>
                                        );
                                    })}
                                    {chooserOn && (
                                        // The chooser stays at the right edge while the
                                        // table scrolls sideways. Each body row has an
                                        // empty cell under it.
                                        <th
                                            ref={chooserCellRef}
                                            className="sticky right-0 z-[1] w-6 bg-[rgb(var(--color-surface-secondary))] p-0 text-center align-middle"
                                        >
                                            <button
                                                type="button"
                                                data-column-chooser-trigger=""
                                                aria-haspopup="dialog"
                                                aria-expanded={chooserOpen}
                                                aria-label={
                                                    chooserChanged
                                                        ? 'Choose columns, changed from the default'
                                                        : 'Choose columns'
                                                }
                                                title="Choose columns"
                                                onClick={() => setChooserOpen((o) => !o)}
                                                className={cn(
                                                    'mx-auto grid h-5 w-5 place-items-center rounded-[3px]',
                                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                                                    chooserChanged || chooserOpen
                                                        ? 'text-[rgb(var(--color-accent))] bg-[rgb(var(--color-accent)/0.12)]'
                                                        : 'text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-surface-hover)/0.06)] hover:text-[rgb(var(--color-text-secondary))]',
                                                )}
                                            >
                                                <Columns3 className="h-3 w-3" />
                                            </button>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
                                        <tr
                                            key={`skeleton-${rowIdx}`}
                                            aria-hidden="true"
                                            className={cn(
                                                rowHeight,
                                                'border-b border-[rgb(var(--color-divider))] last:border-b-0',
                                                ZEBRA,
                                            )}
                                        >
                                            {selection && <td className="w-9" />}
                                            {expandable && <td className="w-9" />}
                                            {visibleColumns.map((col, colIdx) => {
                                                const align = resolveAlign(col);
                                                return (
                                                    <td
                                                        key={col.id}
                                                        className={cn(CELL_PADDING, ALIGN[align])}
                                                    >
                                                        <div
                                                            className={cn(
                                                                'h-2.5 rounded bg-[rgb(var(--color-text-primary)/0.08)] animate-pulse',
                                                                SKELETON_WIDTHS[
                                                                    (rowIdx + colIdx) %
                                                                        SKELETON_WIDTHS.length
                                                                ],
                                                                align === 'right' && 'ml-auto',
                                                                align === 'center' && 'mx-auto',
                                                            )}
                                                        />
                                                    </td>
                                                );
                                            })}
                                            {chooserOn && <td aria-hidden="true" />}
                                        </tr>
                                    ))
                                ) : total === 0 ? (
                                    <tr className="septima-table-empty h-8">
                                        <td
                                            colSpan={colCount}
                                            className="px-1.5 py-0 text-annotation text-[rgb(var(--color-text-tertiary))]"
                                        >
                                            {/* A zero width keeps the empty copy out of the
                                                    column measurement. The copy wraps at the table width. */}
                                            <div className="w-0 min-w-full">
                                                {hasActiveFilters && data.length > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        <span>No rows match these filters.</span>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            onClick={() => changeFilters({})}
                                                        >
                                                            Clear filters
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    empty
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((row) => {
                                        const key = row.id;
                                        const original = row.original;
                                        const isSelected = selectedKey === key;
                                        // A row is expandable only when `expandable` yields
                                        // real content for it (null/undefined → no chevron,
                                        // no expanded row).
                                        const expandedContent = expandable
                                            ? expandable(original)
                                            : null;
                                        const hasExpansion = expandedContent != null;
                                        const isExpanded = hasExpansion && Boolean(expanded[key]);
                                        const clickable = Boolean(onRowClick);
                                        return (
                                            <Fragment key={key}>
                                                <tr
                                                    aria-selected={isSelected || undefined}
                                                    // A clickable row carries the id that
                                                    // `SplitPane` uses to find it again.
                                                    data-sel-id={clickable ? key : undefined}
                                                    tabIndex={clickable ? 0 : undefined}
                                                    onClick={
                                                        clickable
                                                            ? () => onRowClick!(original)
                                                            : undefined
                                                    }
                                                    onKeyDown={
                                                        clickable
                                                            ? (e) => {
                                                                  // A key from a control inside the row
                                                                  // is not a row key.
                                                                  if (e.target !== e.currentTarget)
                                                                      return;
                                                                  if (
                                                                      e.key === 'Enter' ||
                                                                      e.key === ' '
                                                                  ) {
                                                                      e.preventDefault();
                                                                      onRowClick!(original);
                                                                  }
                                                              }
                                                            : undefined
                                                    }
                                                    className={cn(
                                                        rowHeight,
                                                        'border-b border-[rgb(var(--color-divider))] last:border-b-0',
                                                        'transition-colors duration-card-hover',
                                                        isSelected
                                                            ? 'bg-[rgb(var(--color-accent)/0.06)]'
                                                            : cn(
                                                                  ZEBRA,
                                                                  'hover:bg-[rgb(var(--color-row-hover)/0.05)]',
                                                              ),
                                                        clickable &&
                                                            'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                                                        rowClassName?.(original),
                                                    )}
                                                >
                                                    {selection && (
                                                        // Stop BOTH events. The
                                                        // Checkbox stops the
                                                        // click; the row's own
                                                        // Space/Enter handler
                                                        // would otherwise
                                                        // navigate away as the
                                                        // keyboard user ticks
                                                        // the box.
                                                        <td
                                                            className="w-9 px-2 text-center align-middle leading-none"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                        >
                                                            {(selection.isSelectable?.(original) ??
                                                                true) && (
                                                                <Checkbox
                                                                    checked={selection.selectedKeys.has(
                                                                        key,
                                                                    )}
                                                                    onChange={() =>
                                                                        selection.onToggle(
                                                                            key,
                                                                            original,
                                                                        )
                                                                    }
                                                                    aria-label={
                                                                        selection.rowLabel?.(
                                                                            original,
                                                                        ) ?? 'Select row'
                                                                    }
                                                                />
                                                            )}
                                                        </td>
                                                    )}
                                                    {expandable && (
                                                        <td className="w-9 pl-2">
                                                            {hasExpansion ? (
                                                                <button
                                                                    type="button"
                                                                    aria-expanded={isExpanded}
                                                                    aria-label={
                                                                        isExpanded
                                                                            ? 'Collapse row'
                                                                            : 'Expand row'
                                                                    }
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleExpand(key);
                                                                    }}
                                                                    className="grid h-6 w-6 place-items-center rounded text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-surface-secondary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]"
                                                                >
                                                                    <ChevronRight
                                                                        className={cn(
                                                                            'h-4 w-4 transition-transform duration-table-expand',
                                                                            isExpanded &&
                                                                                'rotate-90',
                                                                        )}
                                                                    />
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                    )}
                                                    {visibleColumns.map((col) => {
                                                        const align = resolveAlign(col);
                                                        return (
                                                            <td
                                                                key={col.id}
                                                                className={cn(
                                                                    CELL_PADDING,
                                                                    ALIGN[align],
                                                                    !measuring && CELL_OVERFLOW,
                                                                    measuring &&
                                                                        'whitespace-nowrap',
                                                                    col.numeric &&
                                                                        'font-mono tabular-nums',
                                                                )}
                                                            >
                                                                {col.cell(original)}
                                                            </td>
                                                        );
                                                    })}
                                                    {/* The cell under the column chooser. A row
                                                    paints its fill only behind its cells, so
                                                    without it the zebra and the hover stop
                                                    short of the right edge. */}
                                                    {chooserOn && <td aria-hidden="true" />}
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="border-b border-[rgb(var(--color-divider))] last:border-b-0">
                                                        <td colSpan={colCount} className="p-0">
                                                            <div className="px-3 py-2 bg-[rgb(var(--color-surface-hover)/0.02)]">
                                                                {expandedContent}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Edge fades cue horizontal scroll; ink token reads as a soft
                    shadow over any cell background in both light and dark. */}
                    <div
                        aria-hidden="true"
                        className={cn(
                            'pointer-events-none absolute inset-y-0 left-0 z-20 w-6 bg-gradient-to-r from-[rgb(var(--color-surface-hover)/0.14)] to-transparent transition-opacity duration-card-hover',
                            edges.left ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                    <div
                        aria-hidden="true"
                        className={cn(
                            'pointer-events-none absolute inset-y-0 right-0 z-20 w-6 bg-gradient-to-l from-[rgb(var(--color-surface-hover)/0.14)] to-transparent transition-opacity duration-card-hover',
                            edges.right ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                </div>
            )}
            {openColumn && openSpec && openAnchor && (
                <ColumnFilterPopover
                    key={openColumn.id}
                    anchor={openAnchor}
                    columnLabel={headerLabel(openColumn)}
                    type={openSpec.type}
                    value={filterModel[openColumn.id]}
                    distinct={openDistinct}
                    sort={openSort}
                    canSort={Boolean(openColumn.sortable && openColumn.sortValue)}
                    numeric={Boolean(openColumn.numeric)}
                    onSort={(desc) => handleSortingChange([{ id: openColumn.id, desc }])}
                    onApply={(next) => setColumnFilter(openColumn.id, next)}
                    onClose={closeFilter}
                />
            )}
            {chooserOpen && chooserCellRef.current && (
                <ColumnChooserPopover
                    anchor={chooserCellRef.current}
                    tableLabel={ariaLabel}
                    items={chooserItems(
                        columns,
                        chooserColumns,
                        columnPrefs,
                        visibleIdList,
                        movableIds,
                    )}
                    isDefault={!chooserChanged}
                    onToggle={(id) => setColumnPrefs(toggleColumn(chooserColumns, columnPrefs, id))}
                    onMove={moveBy}
                    onShowAll={() => setColumnPrefs(showAllColumns(chooserColumns, columnPrefs))}
                    onReset={resetLayout}
                    onClose={closeChooser}
                />
            )}
            {pagination && total > 0 && (
                <Pagination
                    page={safePage}
                    pageSize={pageSize}
                    total={total}
                    {...(paginationOptions.pageSizeOptions
                        ? { pageSizeOptions: paginationOptions.pageSizeOptions }
                        : {})}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPage(1);
                    }}
                />
            )}
        </div>
    );
}

// The rows of the column chooser, in the order of the table. A column that
// does not show sits where it would show. Each visible column that can move
// says whether a move up (to the left) or down (to the right) is possible.
function chooserItems<T>(
    columns: readonly DataTableColumn<T>[],
    chooserColumns: readonly ChooserColumn[],
    prefs: ColumnPrefs,
    visibleIdList: readonly string[],
    movableIds: ReadonlySet<string>,
): ColumnChooserItem[] {
    const byId = new Map(columns.map((c) => [c.id, c]));
    const visibleMovable = visibleIdList.filter((id) => movableIds.has(id));
    return orderedColumnIds(chooserColumns, prefs)
        .map((id) => byId.get(id)!)
        .filter((col) => headerLabel(col).trim() !== '')
        .map((col) => {
            const visible = visibleIdList.includes(col.id);
            const at = visible ? visibleMovable.indexOf(col.id) : -1;
            return {
                id: col.id,
                label: headerLabel(col),
                visible,
                canToggle: col.hideable !== false && !(visible && visibleIdList.length === 1),
                canMoveUp: at > 0,
                canMoveDown: at >= 0 && at < visibleMovable.length - 1,
            };
        });
}
